import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LogIn, LogOut, MapPin, Wifi, QrCode, Clock, ShieldAlert, History, CalendarCheck } from "lucide-react";
import { statusMeta, resultMeta, overallMeta, fmtTime, getPosition } from "@/pages/hr/attendanceUtils";
import QrScanner from "@/components/QrScanner";
import FaceCapture from "@/components/FaceCapture";

const PunchResult = ({ punch, title }) => {
  if (!punch) return null;
  const o = overallMeta(punch.overall);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`att-punch-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</span>
        <span className="font-mono text-sm">{fmtTime(punch.server_ts)}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Badge variant="outline" className={o.cls}>{o.label}</Badge>
        {punch.location_result !== "not_required" && (
          <Badge variant="outline" className={resultMeta(punch.location_result).cls}><MapPin className="w-3 h-3 mr-1" />{resultMeta(punch.location_result).label}</Badge>
        )}
        {punch.network_result !== "not_required" && (
          <Badge variant="outline" className={resultMeta(punch.network_result).cls}><Wifi className="w-3 h-3 mr-1" />{resultMeta(punch.network_result).label}</Badge>
        )}
        {punch.qr_result && punch.qr_result !== "not_required" && (
          <Badge variant="outline" className={resultMeta(punch.qr_result).cls}><QrCode className="w-3 h-3 mr-1" />{resultMeta(punch.qr_result).label}</Badge>
        )}
      </div>
      {punch.reasons?.length > 0 && (
        <ul className="mt-1.5 text-xs text-rose-600 list-disc pl-4">{punch.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
      )}
    </div>
  );
};

export default function MyAttendance() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showExc, setShowExc] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [scanFor, setScanFor] = useState(null);
  const [faceFor, setFaceFor] = useState(null);
  const [faceFailed, setFaceFailed] = useState(null);

  const load = () => {
    api.get("/hr/attendance/me/today").then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(formatApiError(e.response?.data?.detail)));
    api.get("/hr/attendance/me/history?limit=30").then(r => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doPunch = async (kind, pos, qr_token, face_token) => {
    setBusy(true);
    try {
      await api.post(`/hr/attendance/me/${kind}`, { ...pos, qr_token, face_token, device: navigator.userAgent });
      toast.success(kind === "clock-in" ? "Clocked in" : "Clocked out");
      setScanFor(null); setFaceFor(null); setFaceFailed(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const afterFace = (kind, pos, face_token) => {
    if (data.policy?.require_qr) { setScanFor({ kind, pos, face_token }); setBusy(false); return; }
    doPunch(kind, pos, null, face_token);
  };

  const punch = async (kind) => {
    setBusy(true);
    try {
      const pos = await getPosition();
      if (pos.location_denied) toast.message("Location unavailable", { description: "Punching without GPS — verification may need an exception." });
      const face = data.policy?.face;
      if (face?.enabled) {
        if (face.enrolled) { setFaceFor({ kind, pos }); setBusy(false); return; }
        if (face.enforcement === "required") {
          setFaceFailed({ kind, pos, reason: "Face enrollment is required before attendance can be verified. Please ask Admin to enroll your face, or request an attendance exception." });
          setBusy(false); return;
        }
        toast.message("Face not enrolled", { description: "Recorded in observe mode." });
        afterFace(kind, pos, null); return;
      }
      afterFace(kind, pos, null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const onFaceCaptured = async (descriptor) => {
    const ctx = faceFor; setFaceFor(null);
    if (!ctx) return;
    setBusy(true);
    const event = ctx.kind === "clock-in" ? "clock_in" : "clock_out";
    try {
      const r = await api.post("/hr/attendance/face/verify", { descriptor, event, device: navigator.userAgent });
      const { result, token, enforcement, score } = r.data;
      if (result === "passed" || enforcement === "observe") {
        if (result === "passed") toast.success(`Face verified (${Math.round((score || 0) * 100)}% match)`);
        else toast.message("Face not matched", { description: "Recorded in observe mode." });
        afterFace(ctx.kind, ctx.pos, token);
      } else {
        setBusy(false);
        setFaceFailed({ kind: ctx.kind, pos: ctx.pos, token, reason: "Face could not be verified. Please try again, or request an attendance exception." });
      }
    } catch (e) { setBusy(false); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitException = async () => {
    if (!explanation.trim()) return toast.error("Please enter an explanation");
    try {
      await api.post(`/hr/attendance/${data.attendance.id}/exception`, { explanation });
      toast.success("Exception submitted for review"); setShowExc(false); setExplanation(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (err) return <div className="p-8 text-center text-slate-500" data-testid="myattendance-error">{err}</div>;
  if (!data) return <div className="p-8 text-center text-slate-400" data-testid="myattendance-loading">Loading…</div>;

  const rec = data.attendance;
  const sched = data.schedule || {};
  const st = statusMeta(rec?.status);
  const needsException = can("attendance:exception_submit") && rec && rec.exception_status !== "pending" &&
    ["clock_in", "clock_out"].some(k => rec[k] && ["exception_required", "failed"].includes(rec[k].overall));

  return (
    <div data-testid="myattendance-page" className="max-w-xl mx-auto">
      <header className="mb-5">
        <div className="caption">Attendance</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">My Attendance</h1>
        <p className="text-slate-500 mt-1 text-sm font-mono">{data.employee_id} · {data.full_name}</p>
      </header>

      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="att-today-card">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold flex items-center gap-1.5"><Clock className="w-4 h-4 text-sky-600" />{data.date}</span>
          {rec && <Badge variant="outline" className={st.cls} data-testid="att-status-badge">{st.label}</Badge>}
        </div>
        <div className="text-sm text-slate-500 mb-4">
          {sched.is_scheduled
            ? <>Scheduled: <span className="font-mono text-slate-700">{sched.scheduled_start || "—"}–{sched.scheduled_end || "—"}</span></>
            : <span className="text-amber-600">Not scheduled to work today</span>}
        </div>

        {data.on_leave && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 mb-4 text-sm text-violet-800 flex items-start gap-2" data-testid="att-on-leave-banner">
            <CalendarCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>On approved leave today — <b>{data.leave?.leave_type_name}</b>{data.leave?.full_day ? " (full day)" : data.leave?.portion ? ` (${data.leave.portion} day)` : ""}. You are not normally expected to clock in today.</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button data-testid="att-clock-in-btn" disabled={busy || !data.can_clock_in}
            onClick={() => punch("clock-in")} className="h-16 text-base bg-emerald-600 hover:bg-emerald-700">
            <LogIn className="w-5 h-5 mr-2" />Clock In
          </Button>
          <Button data-testid="att-clock-out-btn" disabled={busy || !data.can_clock_out}
            onClick={() => punch("clock-out")} className="h-16 text-base bg-slate-900 hover:bg-slate-800">
            <LogOut className="w-5 h-5 mr-2" />Clock Out
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2 text-center">Your location is captured only at punch time. Server time &amp; office network are verified automatically.</p>
      </div>

      {data.policy && data.policy.configured === false && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 text-sm text-amber-700" data-testid="att-no-policy-note">
          No attendance policy is configured for your profile yet — punches are recorded without location, network or QR verification. Please contact Admin if this is unexpected.
        </div>
      )}

      {rec && (
        <div className="space-y-2 mb-4">
          <PunchResult punch={rec.clock_in} title="Clock In" />
          <PunchResult punch={rec.clock_out} title="Clock Out" />
        </div>
      )}

      {needsException && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-4" data-testid="att-exception-prompt">
          <div className="flex items-center gap-2 text-sm text-amber-800 font-medium mb-2"><ShieldAlert className="w-4 h-4" />A punch failed verification</div>
          {!showExc
            ? <Button size="sm" variant="outline" data-testid="att-exception-open" onClick={() => setShowExc(true)} className="border-amber-400 text-amber-800">Submit Exception</Button>
            : <div className="space-y-2">
                <Textarea data-testid="att-exception-text" placeholder="Explain why (e.g. office wifi not whitelisted yet)"
                  value={explanation} onChange={e => setExplanation(e.target.value)} rows={2} />
                <div className="flex gap-2">
                  <Button size="sm" data-testid="att-exception-submit" onClick={submitException} className="bg-amber-600 hover:bg-amber-700">Submit</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowExc(false)}>Cancel</Button>
                </div>
              </div>}
        </div>
      )}
      {rec?.exception_status === "pending" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 text-sm text-amber-700" data-testid="att-exception-pending">
          Exception submitted — awaiting admin review.
        </div>
      )}

      {faceFailed && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 mb-4" data-testid="att-face-failed">
          <div className="flex items-center gap-2 text-sm text-rose-700 font-medium mb-2"><ShieldAlert className="w-4 h-4" />{faceFailed.reason}</div>
          <div className="flex gap-2">
            {data.policy?.face?.enrolled && (
              <Button size="sm" variant="outline" data-testid="att-face-tryagain"
                onClick={() => { const c = faceFailed; setFaceFailed(null); setFaceFor({ kind: c.kind, pos: c.pos }); }}>Try Again</Button>
            )}
            <Button size="sm" data-testid="att-face-exception" className="bg-amber-600 hover:bg-amber-700"
              onClick={() => { const c = faceFailed; setFaceFailed(null); afterFace(c.kind, c.pos, c.token || null); }}>
              Request Attendance Exception
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="att-history">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold flex items-center gap-1.5"><History className="w-4 h-4 text-slate-400" />Recent Attendance</div>
        {history.length === 0
          ? <div className="px-4 py-6 text-center text-sm text-slate-400">No records yet</div>
          : history.map(h => (
            <div key={h.id} className="flex items-center justify-between px-4 py-2 border-b border-slate-50 last:border-0 text-sm" data-testid={`att-hist-${h.date}`}>
              <span className="font-mono">{h.date}</span>
              <span className="text-xs text-slate-500 font-mono">{fmtTime(h.clock_in?.server_ts)} – {fmtTime(h.clock_out?.server_ts)}</span>
              <Badge variant="outline" className={statusMeta(h.status).cls}>{statusMeta(h.status).label}</Badge>
            </div>
          ))}
      </div>

      <QrScanner open={!!scanFor} allowFallback={data.policy?.allow_exception}
        onScan={(token) => { const s = scanFor; setScanFor(null); if (s) doPunch(s.kind, s.pos, token, s.face_token); }}
        onClose={() => setScanFor(null)}
        onFallback={() => { const s = scanFor; setScanFor(null); if (s) doPunch(s.kind, s.pos, null, s.face_token); }} />

      <FaceCapture open={!!faceFor} mode="verify" title="Verify Your Face"
        onCapture={onFaceCaptured} onClose={() => { setFaceFor(null); setBusy(false); }} />
    </div>
  );
}
